import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    FileText,
    ShieldCheck,
    AlertCircle,
    CheckCircle2,
    Loader2,
    ArrowLeft,
} from 'lucide-react';
import { API_V1_BASE_URL } from '../config/api';

interface TncData {
    id: string;
    version: string;
    content: string;
}

interface LocationState {
    email?: string;
    password?: string;
    tnc?: TncData;
    tncAcceptanceToken?: string;
}

const TenantTnc: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const state = location.state as LocationState | null;
    const email = state?.email || '';
    const password = state?.password || '';
    const tnc = state?.tnc;
    const tncAcceptanceToken = state?.tncAcceptanceToken;
    const [isAccepted, setIsAccepted] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const handleAccept = async () => {
        if (!isAccepted) {
            setError('Please accept the Terms and Conditions to continue.');
            return;
        }
        if (!email || !tnc) {
            setError('T&C information is missing. Please login again.');
            return;
        }
        setIsLoading(true);
        setError('');
        try {
            if (!tncAcceptanceToken) {
                setError('T&C acceptance session has expired. Please login again.');
                return;
            }
            const response = await fetch(`${API_V1_BASE_URL}/auth/accept-tenant-tnc`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${tncAcceptanceToken}`,
                },
                body: JSON.stringify({ tncVersion: tnc.version }),
            });
            const result = await response.json().catch(() => null);
            if (!response.ok || !result?.success || !result?.data) {
                throw new Error(result?.message || 'Unable to accept Terms and Conditions.');
            }
            navigate('/login', {
                state: { email, password, resetSuccess: false },
                replace: true,
            });
            return;
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Unable to accept Terms and Conditions.');
        } finally {
            setIsLoading(false);
        }
    };

    /* ============ Fallback: no T&C data ============ */
    if (!tnc) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-100 to-indigo-100 p-6">
                <div className="bg-white rounded-2xl shadow-xl shadow-indigo-200/50 p-10 max-w-lg w-full text-center border border-slate-200">
                    <div className="mx-auto w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center mb-4">
                        <AlertCircle size={26} className="text-amber-600" />
                    </div>
                    <h2 className="text-xl font-bold text-slate-900 mb-2">
                        Terms and Conditions unavailable
                    </h2>
                    <p className="text-sm text-slate-500 mb-6">
                        Please login again to load the current Terms and Conditions.
                    </p>
                    <button
                        onClick={() => navigate('/login')}
                        className="inline-flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition font-medium text-sm shadow-lg shadow-indigo-600/20"
                    >
                        <ArrowLeft size={15} /> Back to Login
                    </button>
                </div>
            </div>
        );
    }

    /* ============ Main T&C page ============ */
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-100 via-indigo-50 to-violet-100 py-10 px-4">
            <div className="max-w-4xl mx-auto">
                {/* Page heading outside card (subtle) */}
                <div className="text-center mb-6">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-100 text-indigo-700 text-xs font-medium">
                        <ShieldCheck size={13} /> Secure Agreement
                    </div>
                </div>

                <div className="bg-white rounded-2xl shadow-xl shadow-indigo-200/40 border border-slate-200 overflow-hidden">
                    {/* ===== Header ===== */}
                    <div className="relative px-8 py-6 bg-gradient-to-r from-indigo-600 to-violet-600 text-white">
                        <div className="flex items-start gap-4">
                            <div className="w-11 h-11 rounded-xl bg-white/15 backdrop-blur border border-white/20 flex items-center justify-center shrink-0">
                                <FileText size={20} className="text-white" />
                            </div>
                            <div className="flex-1">
                                <h1 className="text-2xl font-bold tracking-tight">
                                    Terms and Conditions
                                </h1>
                                <div className="flex flex-wrap items-center gap-2 mt-2">
                                    <span className="inline-flex items-center rounded-full bg-white/15 border border-white/20 px-3 py-0.5 text-xs font-semibold">
                                        Version {tnc.version}
                                    </span>
                                    {email && (
                                        <span className="text-xs text-indigo-100/90">
                                            Account: <span className="font-medium text-white">{email}</span>
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ===== Body ===== */}
                    <div className="px-8 py-6">
                        {/* Content */}
                        <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-6 max-h-[480px] overflow-y-auto whitespace-pre-wrap text-sm leading-7 text-slate-700 shadow-inner">
                            {tnc.content}
                        </div>

                        {/* Error */}
                        {error && (
                            <div className="mt-4 flex items-start gap-2.5 p-3 rounded-xl border border-red-200 bg-red-50 text-sm text-red-700">
                                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                                <span>{error}</span>
                            </div>
                        )}

                        {/* Agreement checkbox */}
                        <label
                            className={`mt-6 flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${isAccepted
                                    ? 'border-indigo-300 bg-indigo-50/60'
                                    : 'border-slate-200 bg-white hover:bg-slate-50'
                                }`}
                        >
                            <input
                                type="checkbox"
                                checked={isAccepted}
                                onChange={(e) => setIsAccepted(e.target.checked)}
                                className="mt-0.5 w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500/40"
                            />
                            <div>
                                <span className="text-sm font-medium text-slate-800">
                                    I have read and agree to the Terms and Conditions shown above.
                                </span>
                                <p className="text-xs text-slate-500 mt-1">
                                    You must accept to continue using the platform.
                                </p>
                            </div>
                        </label>

                        {/* Actions */}
                        <div className="mt-6 flex flex-col-reverse sm:flex-row gap-3">
                            <button
                                type="button"
                                onClick={() => navigate('/login')}
                                disabled={isLoading}
                                className="w-full sm:w-1/3 bg-white text-slate-700 py-3 rounded-xl border border-slate-300 hover:bg-slate-50 transition font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Cancel
                            </button>

                            <button
                                type="button"
                                onClick={handleAccept}
                                disabled={isLoading || !isAccepted}
                                className="w-full sm:flex-1 inline-flex items-center justify-center gap-2 bg-indigo-600 text-white py-3 rounded-xl hover:bg-indigo-700 active:bg-indigo-800 transition font-medium text-sm shadow-lg shadow-indigo-600/20 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 size={16} className="animate-spin" />
                                        Processing...
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle2 size={16} />
                                        Accept and Continue
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* ===== Footer ===== */}
                    <div className="px-8 py-4 border-t border-slate-100 bg-slate-50/60 flex items-center justify-center gap-1.5 text-xs text-slate-400">
                        <ShieldCheck size={13} />
                        Secured by enterprise-grade encryption
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TenantTnc;